import { Box } from '@osmosis/tailwind-ui/components/Box';
import { H2, P } from '@osmosis/tailwind-ui/components/Text';
import { NoteContent } from '@elsevier-health/osmosis-shared/types/learndb/learnData';
import Image from 'next/image';
import { bg200, borderColorBlue600, text3 } from '@osmosis/tailwind-ui/styles';
import { Duration } from '../../../../components/video/Duration';
import { useRouter } from 'next/navigation';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { FormattedMessage, useIntl } from 'react-intl';

interface NotesPageRelatedVideosBlockProps {
  contents?: NoteContent[];
}

/**
 * Renders a block displaying related video links for the notes page.
 * @param {NotesPageRelatedVideosBlockProps} props - The props for the component.
 */
const NotesPageRelatedVideosBlock = ({
  contents,
}: NotesPageRelatedVideosBlockProps) => {
  const { push } = useRouter();
  const { formatMessage } = useIntl();

  if (
    !contents ||
    contents.length === 0 ||
    contents.filter((c) => c.thumbnail).length === 0
  )
    return null;

  return (
    <Box className={'justify-items-start'}>
      <H2 className="pb-3">
        <FormattedMessage
          id="notesPage.related.title"
          defaultMessage="Related Videos"
        />
      </H2>
      <Box className="mt-2 flex-row w-full">
        {contents.map((content, index) => {
          if (!content.thumbnail) return null;
          const pageUrl = `/learn/${content.learnPage.replace(/\s/g, '_')}`;

          return (
            <Button
              data-testid={`related_video_${pageUrl}`}
              variant={'transparent'}
              onClick={() => push(pageUrl)}
              key={index}
              className={`flex justify-start mb-2 w-full p-2 rounded-md flex-row hover:${bg200} items-center`}
              aria-label={formatMessage(
                {
                  id: 'notesPage.relatedVideo.label',
                  defaultMessage: 'Related video: {title}',
                },
                { title: content.learnPage },
              )}
            >
              <Box className="max-w-[150px] min-w-[100px] flex-1">
                <Image
                  alt={content.learnPage}
                  src={content.thumbnail || ''}
                  loading="lazy"
                  className={`border-2 rounded-md ${borderColorBlue600} w-full h-auto`}
                  sizes="100vw"
                  width={500}
                  height={300}
                />
                <Box className="flex z-10 mt-[-30px] justify-end mr-[6px]">
                  <Duration duration={content.duration} />
                </Box>
              </Box>
              <P
                className={`${text3} ml-4 text-wrap overflow-hidden text-left flex-1 shrink`}
              >
                {content.learnPage}
              </P>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default NotesPageRelatedVideosBlock;
